/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Tessera Backend API
 * Tessera backend application with Bearer token authentication and multi-tenant support
 */
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  InvalidateOptions,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';

import type { BodyType, ErrorType } from '../api-instance';
import { apiInstance } from '../api-instance';
import type {
  CancelMessagePathParameters,
  CancelMessageStreamingPathParameters,
  DeleteMessagePathParameters,
  EditMessagePathParameters,
  GetConversationMessagesPathParameters,
  ProcessFeedbackPathParameters,
  StreamMessagePathParameters,
  TLChatMessageResponse,
  TLConversationMessagesResponse,
  TLEditMessageRequest,
  TLFeedbackRequest,
  TLHTTPValidationError,
  TLQueuedMessageResponse,
  TLSendMessageRequest,
  TLSuccessResponse,
} from './tessera-backend-api.schemas';

type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];

const withQueryKey = <T extends object, K>(query: T, queryKey: K): T & { queryKey: K } => {
  const result = { queryKey } as T & { queryKey: K };
  for (const key of Object.keys(query)) {
    // The explicit queryKey always wins, matching the previous
    // `{ ...query, queryKey }` spread where it was set last.
    if (key === 'queryKey') continue;
    Object.defineProperty(result, key, {
      enumerable: true,
      configurable: true,
      get: () => (query as Record<string, unknown>)[key],
    });
  }
  return result;
};

/**
 * @summary Cancel Message
 */
export const cancelMessage = (
  { conversationId, messageId }: CancelMessagePathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLSuccessResponse>(
    { url: `/conversations/cancel/${conversationId}/message/${messageId}`, method: 'POST', signal },
    options,
  );
};

export const getCancelMessageQueryKey = ({ conversationId, messageId }: CancelMessagePathParameters) => {
  return ['POST', 'conversations', 'cancel', conversationId, 'message', messageId] as const;
};

export const getCancelMessageQueryOptions = <
  TData = Awaited<ReturnType<typeof cancelMessage>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { conversationId, messageId }: CancelMessagePathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof cancelMessage>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getCancelMessageQueryKey({ conversationId, messageId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof cancelMessage>>> = ({ signal }) =>
    cancelMessage({ conversationId, messageId }, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: conversationId !== null && conversationId !== undefined && messageId !== null && messageId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof cancelMessage>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type CancelMessageQueryResult = NonNullable<Awaited<ReturnType<typeof cancelMessage>>>;
export type CancelMessageQueryError = ErrorType<TLHTTPValidationError>;

export function useCancelMessage<
  TData = Awaited<ReturnType<typeof cancelMessage>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: CancelMessagePathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof cancelMessage>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof cancelMessage>>,
          TError,
          Awaited<ReturnType<typeof cancelMessage>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useCancelMessage<
  TData = Awaited<ReturnType<typeof cancelMessage>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: CancelMessagePathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof cancelMessage>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof cancelMessage>>,
          TError,
          Awaited<ReturnType<typeof cancelMessage>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useCancelMessage<
  TData = Awaited<ReturnType<typeof cancelMessage>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: CancelMessagePathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof cancelMessage>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Cancel Message
 */

export function useCancelMessage<
  TData = Awaited<ReturnType<typeof cancelMessage>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { conversationId, messageId }: CancelMessagePathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof cancelMessage>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getCancelMessageQueryOptions({ conversationId, messageId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Cancel Message
 */
export const prefetchCancelMessageQuery = async <
  TData = Awaited<ReturnType<typeof cancelMessage>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { conversationId, messageId }: CancelMessagePathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof cancelMessage>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getCancelMessageQueryOptions({ conversationId, messageId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Cancel Message
 */
export const invalidateCancelMessage = async (
  queryClient: QueryClient,
  { conversationId, messageId }: CancelMessagePathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getCancelMessageQueryKey({ conversationId, messageId }) }, options);

  return queryClient;
};

/**
 * Send a message with streaming support. EDITOR_CHAT / REMEDIATION_CHAT use the same pipeline as POST /conversations/editor.
 *
 * While a turn is still processing the follow-up is queued server-side and a QueuedMessageResponse is returned;
 * clients can distinguish that response by the presence of queued_message_id. It is auto-sent when the turn
 * completes. Auth applies the same existing roles as elsewhere: CHAT_ACCESS for normal chat types, EDITOR_ACCESS
 * when chat_type is EDITOR_CHAT or REMEDIATION_CHAT.
 * @summary Send Message
 */
export const sendMessage = (
  tLSendMessageRequest: BodyType<TLSendMessageRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLChatMessageResponse | TLQueuedMessageResponse>(
    {
      url: `/conversations/messages`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLSendMessageRequest,
      signal,
    },
    options,
  );
};

export const getSendMessageQueryKey = (tLSendMessageRequest?: BodyType<TLSendMessageRequest>) => {
  return ['POST', 'conversations', 'messages', tLSendMessageRequest] as const;
};

export const getSendMessageQueryOptions = <
  TData = Awaited<ReturnType<typeof sendMessage>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLSendMessageRequest: BodyType<TLSendMessageRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof sendMessage>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getSendMessageQueryKey(tLSendMessageRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof sendMessage>>> = ({ signal }) =>
    sendMessage(tLSendMessageRequest, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof sendMessage>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type SendMessageQueryResult = NonNullable<Awaited<ReturnType<typeof sendMessage>>>;
export type SendMessageQueryError = ErrorType<TLHTTPValidationError>;

export function useSendMessage<
  TData = Awaited<ReturnType<typeof sendMessage>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLSendMessageRequest: BodyType<TLSendMessageRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof sendMessage>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof sendMessage>>,
          TError,
          Awaited<ReturnType<typeof sendMessage>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useSendMessage<
  TData = Awaited<ReturnType<typeof sendMessage>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLSendMessageRequest: BodyType<TLSendMessageRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof sendMessage>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof sendMessage>>,
          TError,
          Awaited<ReturnType<typeof sendMessage>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useSendMessage<
  TData = Awaited<ReturnType<typeof sendMessage>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLSendMessageRequest: BodyType<TLSendMessageRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof sendMessage>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Send Message
 */

export function useSendMessage<
  TData = Awaited<ReturnType<typeof sendMessage>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLSendMessageRequest: BodyType<TLSendMessageRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof sendMessage>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getSendMessageQueryOptions(tLSendMessageRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Send Message
 */
export const prefetchSendMessageQuery = async <
  TData = Awaited<ReturnType<typeof sendMessage>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  tLSendMessageRequest: BodyType<TLSendMessageRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof sendMessage>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getSendMessageQueryOptions(tLSendMessageRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Send Message
 */
export const invalidateSendMessage = async (
  queryClient: QueryClient,
  tLSendMessageRequest: BodyType<TLSendMessageRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getSendMessageQueryKey(tLSendMessageRequest) }, options);

  return queryClient;
};

/**
 * SSE stream for real-time message tokens.
 * @summary Stream Message
 */
export const streamMessage = (
  { messageId }: StreamMessagePathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<unknown>({ url: `/conversations/stream/${messageId}`, method: 'GET', signal }, options);
};

export const getStreamMessageQueryKey = ({ messageId }: StreamMessagePathParameters) => {
  return ['conversations', 'stream', messageId] as const;
};

export const getStreamMessageQueryOptions = <
  TData = Awaited<ReturnType<typeof streamMessage>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { messageId }: StreamMessagePathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof streamMessage>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getStreamMessageQueryKey({ messageId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof streamMessage>>> = ({ signal }) =>
    streamMessage({ messageId }, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: messageId !== null && messageId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof streamMessage>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type StreamMessageQueryResult = NonNullable<Awaited<ReturnType<typeof streamMessage>>>;
export type StreamMessageQueryError = ErrorType<TLHTTPValidationError>;

export function useStreamMessage<
  TData = Awaited<ReturnType<typeof streamMessage>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: StreamMessagePathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof streamMessage>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof streamMessage>>,
          TError,
          Awaited<ReturnType<typeof streamMessage>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useStreamMessage<
  TData = Awaited<ReturnType<typeof streamMessage>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: StreamMessagePathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof streamMessage>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof streamMessage>>,
          TError,
          Awaited<ReturnType<typeof streamMessage>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useStreamMessage<
  TData = Awaited<ReturnType<typeof streamMessage>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: StreamMessagePathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof streamMessage>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Stream Message
 */

export function useStreamMessage<
  TData = Awaited<ReturnType<typeof streamMessage>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { messageId }: StreamMessagePathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof streamMessage>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getStreamMessageQueryOptions({ messageId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Stream Message
 */
export const prefetchStreamMessageQuery = async <
  TData = Awaited<ReturnType<typeof streamMessage>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { messageId }: StreamMessagePathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof streamMessage>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getStreamMessageQueryOptions({ messageId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Stream Message
 */
export const invalidateStreamMessage = async (
  queryClient: QueryClient,
  { messageId }: StreamMessagePathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getStreamMessageQueryKey({ messageId }) }, options);

  return queryClient;
};

/**
 * @summary Delete Message
 */
export const deleteMessage = (
  { conversationId, messageId }: DeleteMessagePathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLSuccessResponse>(
    { url: `/conversations/${conversationId}/message/${messageId}`, method: 'DELETE', signal },
    options,
  );
};

export const getDeleteMessageQueryKey = ({ conversationId, messageId }: DeleteMessagePathParameters) => {
  return ['DELETE', 'conversations', conversationId, 'message', messageId] as const;
};

export const getDeleteMessageQueryOptions = <
  TData = Awaited<ReturnType<typeof deleteMessage>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { conversationId, messageId }: DeleteMessagePathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteMessage>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getDeleteMessageQueryKey({ conversationId, messageId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof deleteMessage>>> = ({ signal }) =>
    deleteMessage({ conversationId, messageId }, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: conversationId !== null && conversationId !== undefined && messageId !== null && messageId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof deleteMessage>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type DeleteMessageQueryResult = NonNullable<Awaited<ReturnType<typeof deleteMessage>>>;
export type DeleteMessageQueryError = ErrorType<TLHTTPValidationError>;

export function useDeleteMessage<
  TData = Awaited<ReturnType<typeof deleteMessage>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: DeleteMessagePathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteMessage>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof deleteMessage>>,
          TError,
          Awaited<ReturnType<typeof deleteMessage>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useDeleteMessage<
  TData = Awaited<ReturnType<typeof deleteMessage>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: DeleteMessagePathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteMessage>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof deleteMessage>>,
          TError,
          Awaited<ReturnType<typeof deleteMessage>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useDeleteMessage<
  TData = Awaited<ReturnType<typeof deleteMessage>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: DeleteMessagePathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteMessage>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Delete Message
 */

export function useDeleteMessage<
  TData = Awaited<ReturnType<typeof deleteMessage>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { conversationId, messageId }: DeleteMessagePathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteMessage>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getDeleteMessageQueryOptions({ conversationId, messageId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Delete Message
 */
export const prefetchDeleteMessageQuery = async <
  TData = Awaited<ReturnType<typeof deleteMessage>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { conversationId, messageId }: DeleteMessagePathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteMessage>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getDeleteMessageQueryOptions({ conversationId, messageId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Delete Message
 */
export const invalidateDeleteMessage = async (
  queryClient: QueryClient,
  { conversationId, messageId }: DeleteMessagePathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getDeleteMessageQueryKey({ conversationId, messageId }) }, options);

  return queryClient;
};

/**
 * Edit a message: deletes the original and all following messages/programs, then re-sends with new content.
 * @summary Edit Message
 */
export const editMessage = (
  { conversationId, messageId }: EditMessagePathParameters,
  tLEditMessageRequest: BodyType<TLEditMessageRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLChatMessageResponse>(
    {
      url: `/conversations/${conversationId}/message/${messageId}`,
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      data: tLEditMessageRequest,
      signal,
    },
    options,
  );
};

export const getEditMessageQueryKey = (
  { conversationId, messageId }: EditMessagePathParameters,
  tLEditMessageRequest?: BodyType<TLEditMessageRequest>,
) => {
  return ['PUT', 'conversations', conversationId, 'message', messageId, tLEditMessageRequest] as const;
};

export const getEditMessageQueryOptions = <
  TData = Awaited<ReturnType<typeof editMessage>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { conversationId, messageId }: EditMessagePathParameters,
  tLEditMessageRequest: BodyType<TLEditMessageRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof editMessage>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getEditMessageQueryKey({ conversationId, messageId }, tLEditMessageRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof editMessage>>> = ({ signal }) =>
    editMessage({ conversationId, messageId }, tLEditMessageRequest, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: conversationId !== null && conversationId !== undefined && messageId !== null && messageId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof editMessage>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type EditMessageQueryResult = NonNullable<Awaited<ReturnType<typeof editMessage>>>;
export type EditMessageQueryError = ErrorType<TLHTTPValidationError>;

export function useEditMessage<
  TData = Awaited<ReturnType<typeof editMessage>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: EditMessagePathParameters,
  tLEditMessageRequest: BodyType<TLEditMessageRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof editMessage>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof editMessage>>,
          TError,
          Awaited<ReturnType<typeof editMessage>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useEditMessage<
  TData = Awaited<ReturnType<typeof editMessage>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: EditMessagePathParameters,
  tLEditMessageRequest: BodyType<TLEditMessageRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof editMessage>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof editMessage>>,
          TError,
          Awaited<ReturnType<typeof editMessage>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useEditMessage<
  TData = Awaited<ReturnType<typeof editMessage>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: EditMessagePathParameters,
  tLEditMessageRequest: BodyType<TLEditMessageRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof editMessage>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Edit Message
 */

export function useEditMessage<
  TData = Awaited<ReturnType<typeof editMessage>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { conversationId, messageId }: EditMessagePathParameters,
  tLEditMessageRequest: BodyType<TLEditMessageRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof editMessage>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getEditMessageQueryOptions({ conversationId, messageId }, tLEditMessageRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Edit Message
 */
export const prefetchEditMessageQuery = async <
  TData = Awaited<ReturnType<typeof editMessage>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { conversationId, messageId }: EditMessagePathParameters,
  tLEditMessageRequest: BodyType<TLEditMessageRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof editMessage>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getEditMessageQueryOptions({ conversationId, messageId }, tLEditMessageRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Edit Message
 */
export const invalidateEditMessage = async (
  queryClient: QueryClient,
  { conversationId, messageId }: EditMessagePathParameters,
  tLEditMessageRequest: BodyType<TLEditMessageRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getEditMessageQueryKey({ conversationId, messageId }, tLEditMessageRequest) },
    options,
  );

  return queryClient;
};

/**
 * @summary Get Conversation Messages
 */
export const getConversationMessages = (
  { conversationId }: GetConversationMessagesPathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLConversationMessagesResponse>(
    { url: `/conversations/${conversationId}/messages`, method: 'GET', signal },
    options,
  );
};

export const getGetConversationMessagesQueryKey = ({ conversationId }: GetConversationMessagesPathParameters) => {
  return ['conversations', conversationId, 'messages'] as const;
};

export const getGetConversationMessagesQueryOptions = <
  TData = Awaited<ReturnType<typeof getConversationMessages>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { conversationId }: GetConversationMessagesPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getConversationMessages>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetConversationMessagesQueryKey({ conversationId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getConversationMessages>>> = ({ signal }) =>
    getConversationMessages({ conversationId }, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: conversationId !== null && conversationId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof getConversationMessages>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type GetConversationMessagesQueryResult = NonNullable<Awaited<ReturnType<typeof getConversationMessages>>>;
export type GetConversationMessagesQueryError = ErrorType<TLHTTPValidationError>;

export function useGetConversationMessages<
  TData = Awaited<ReturnType<typeof getConversationMessages>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetConversationMessagesPathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getConversationMessages>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getConversationMessages>>,
          TError,
          Awaited<ReturnType<typeof getConversationMessages>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetConversationMessages<
  TData = Awaited<ReturnType<typeof getConversationMessages>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetConversationMessagesPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getConversationMessages>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getConversationMessages>>,
          TError,
          Awaited<ReturnType<typeof getConversationMessages>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetConversationMessages<
  TData = Awaited<ReturnType<typeof getConversationMessages>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: GetConversationMessagesPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getConversationMessages>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Conversation Messages
 */

export function useGetConversationMessages<
  TData = Awaited<ReturnType<typeof getConversationMessages>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { conversationId }: GetConversationMessagesPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getConversationMessages>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetConversationMessagesQueryOptions({ conversationId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get Conversation Messages
 */
export const prefetchGetConversationMessagesQuery = async <
  TData = Awaited<ReturnType<typeof getConversationMessages>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { conversationId }: GetConversationMessagesPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getConversationMessages>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetConversationMessagesQueryOptions({ conversationId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get Conversation Messages
 */
export const invalidateGetConversationMessages = async (
  queryClient: QueryClient,
  { conversationId }: GetConversationMessagesPathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getGetConversationMessagesQueryKey({ conversationId }) }, options);

  return queryClient;
};

/**
 * @summary Cancel Message Streaming
 */
export const cancelMessageStreaming = (
  { conversationId, messageId }: CancelMessageStreamingPathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLSuccessResponse>(
    { url: `/conversations/${conversationId}/messages/${messageId}/cancel`, method: 'POST', signal },
    options,
  );
};

export const getCancelMessageStreamingQueryKey = ({
  conversationId,
  messageId,
}: CancelMessageStreamingPathParameters) => {
  return ['POST', 'conversations', conversationId, 'messages', messageId, 'cancel'] as const;
};

export const getCancelMessageStreamingQueryOptions = <
  TData = Awaited<ReturnType<typeof cancelMessageStreaming>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { conversationId, messageId }: CancelMessageStreamingPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof cancelMessageStreaming>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getCancelMessageStreamingQueryKey({ conversationId, messageId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof cancelMessageStreaming>>> = ({ signal }) =>
    cancelMessageStreaming({ conversationId, messageId }, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: conversationId !== null && conversationId !== undefined && messageId !== null && messageId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof cancelMessageStreaming>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type CancelMessageStreamingQueryResult = NonNullable<Awaited<ReturnType<typeof cancelMessageStreaming>>>;
export type CancelMessageStreamingQueryError = ErrorType<TLHTTPValidationError>;

export function useCancelMessageStreaming<
  TData = Awaited<ReturnType<typeof cancelMessageStreaming>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: CancelMessageStreamingPathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof cancelMessageStreaming>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof cancelMessageStreaming>>,
          TError,
          Awaited<ReturnType<typeof cancelMessageStreaming>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useCancelMessageStreaming<
  TData = Awaited<ReturnType<typeof cancelMessageStreaming>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: CancelMessageStreamingPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof cancelMessageStreaming>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof cancelMessageStreaming>>,
          TError,
          Awaited<ReturnType<typeof cancelMessageStreaming>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useCancelMessageStreaming<
  TData = Awaited<ReturnType<typeof cancelMessageStreaming>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: CancelMessageStreamingPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof cancelMessageStreaming>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Cancel Message Streaming
 */

export function useCancelMessageStreaming<
  TData = Awaited<ReturnType<typeof cancelMessageStreaming>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { conversationId, messageId }: CancelMessageStreamingPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof cancelMessageStreaming>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getCancelMessageStreamingQueryOptions({ conversationId, messageId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Cancel Message Streaming
 */
export const prefetchCancelMessageStreamingQuery = async <
  TData = Awaited<ReturnType<typeof cancelMessageStreaming>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { conversationId, messageId }: CancelMessageStreamingPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof cancelMessageStreaming>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getCancelMessageStreamingQueryOptions({ conversationId, messageId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Cancel Message Streaming
 */
export const invalidateCancelMessageStreaming = async (
  queryClient: QueryClient,
  { conversationId, messageId }: CancelMessageStreamingPathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getCancelMessageStreamingQueryKey({ conversationId, messageId }) },
    options,
  );

  return queryClient;
};

/**
 * Save user feedback for a specific message.
 * @summary Process Feedback
 */
export const processFeedback = (
  { conversationId, messageId }: ProcessFeedbackPathParameters,
  tLFeedbackRequest: BodyType<TLFeedbackRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLSuccessResponse>(
    {
      url: `/conversations/${conversationId}/messages/${messageId}/feedback`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLFeedbackRequest,
      signal,
    },
    options,
  );
};

export const getProcessFeedbackQueryKey = (
  { conversationId, messageId }: ProcessFeedbackPathParameters,
  tLFeedbackRequest?: BodyType<TLFeedbackRequest>,
) => {
  return ['POST', 'conversations', conversationId, 'messages', messageId, 'feedback', tLFeedbackRequest] as const;
};

export const getProcessFeedbackQueryOptions = <
  TData = Awaited<ReturnType<typeof processFeedback>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { conversationId, messageId }: ProcessFeedbackPathParameters,
  tLFeedbackRequest: BodyType<TLFeedbackRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof processFeedback>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getProcessFeedbackQueryKey({ conversationId, messageId }, tLFeedbackRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof processFeedback>>> = ({ signal }) =>
    processFeedback({ conversationId, messageId }, tLFeedbackRequest, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: conversationId !== null && conversationId !== undefined && messageId !== null && messageId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof processFeedback>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type ProcessFeedbackQueryResult = NonNullable<Awaited<ReturnType<typeof processFeedback>>>;
export type ProcessFeedbackQueryError = ErrorType<TLHTTPValidationError>;

export function useProcessFeedback<
  TData = Awaited<ReturnType<typeof processFeedback>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ProcessFeedbackPathParameters,
  tLFeedbackRequest: BodyType<TLFeedbackRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof processFeedback>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof processFeedback>>,
          TError,
          Awaited<ReturnType<typeof processFeedback>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useProcessFeedback<
  TData = Awaited<ReturnType<typeof processFeedback>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ProcessFeedbackPathParameters,
  tLFeedbackRequest: BodyType<TLFeedbackRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof processFeedback>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof processFeedback>>,
          TError,
          Awaited<ReturnType<typeof processFeedback>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useProcessFeedback<
  TData = Awaited<ReturnType<typeof processFeedback>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ProcessFeedbackPathParameters,
  tLFeedbackRequest: BodyType<TLFeedbackRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof processFeedback>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Process Feedback
 */

export function useProcessFeedback<
  TData = Awaited<ReturnType<typeof processFeedback>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { conversationId, messageId }: ProcessFeedbackPathParameters,
  tLFeedbackRequest: BodyType<TLFeedbackRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof processFeedback>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getProcessFeedbackQueryOptions({ conversationId, messageId }, tLFeedbackRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Process Feedback
 */
export const prefetchProcessFeedbackQuery = async <
  TData = Awaited<ReturnType<typeof processFeedback>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { conversationId, messageId }: ProcessFeedbackPathParameters,
  tLFeedbackRequest: BodyType<TLFeedbackRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof processFeedback>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getProcessFeedbackQueryOptions({ conversationId, messageId }, tLFeedbackRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Process Feedback
 */
export const invalidateProcessFeedback = async (
  queryClient: QueryClient,
  { conversationId, messageId }: ProcessFeedbackPathParameters,
  tLFeedbackRequest: BodyType<TLFeedbackRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getProcessFeedbackQueryKey({ conversationId, messageId }, tLFeedbackRequest) },
    options,
  );

  return queryClient;
};
